import React, { useState, useEffect, useRef } from 'react';
import { compose } from 'recompose';
import { GoogleMap, Marker } from "@react-google-maps/api";
import { withStyles } from '@material-ui/core/styles';
import { MapControl, MapControlButton } from 'components/map';
import AutoComplete from 'components/underwrite/acquisition/import/importOpportunities/LocationVerification/AutoComplete';

const US_CENTER_LAT = 37.0902;
const US_CENTER_LNG = -95.7129;

const LocationVerificationMap = ({ classes, currentPin, updateLatLng, autoCompleteEnabled, replaceCurrentAddress }) => {
  const hasPin = currentPin?.lat && currentPin?.lng;
  const [mapInitialized, setMapInitialized] = useState(false);
  const [autoCompletePlace, setAutoCompletePlace] = useState(null);
  const map = useRef(null);
  const autoCompleteRef = useRef(null);

  useEffect(() => {
    if (!mapInitialized) return;
    let lat = US_CENTER_LAT;
    let lng = US_CENTER_LNG;
    let zoom = 4;
    if (hasPin) {
      lat = currentPin.lat;
      lng = currentPin.lng;
      zoom = 17;
    }
    const newCenter = new window.google.maps.LatLng(lat, lng);
    if (!newCenter.equals(map.current.getCenter())) {
      map.current.panTo(newCenter);
    }
    if (!currentPin.maintainZoom && zoom !== map.current.getZoom()) {
      map.current.setZoom(zoom);
    }
  }, [hasPin, currentPin.lat, currentPin.lng, currentPin.maintainZoom, mapInitialized]);

  useEffect(() => {
    if (!autoCompletePlace || !mapInitialized) return;
    let zoom = 17;
    const location = getPlaceLatLng(autoCompletePlace);
    const newCenter = new window.google.maps.LatLng(location.lat, location.lng);
    if (!newCenter.equals(map.current.getCenter())) {
      map.current.panTo(newCenter);
    }
    if (zoom !== map.current.getZoom()) {
      map.current.setZoom(zoom);
    }
  }, [autoCompletePlace, mapInitialized]);

  const onMarkerDragEnd = args => {
    const { latLng } = args;
    updateLatLng(latLng.lat(), latLng.lng());
  }

  const onMapClick = args => {
    if (args.latLng) {
      updateLatLng(args.latLng.lat(), args.latLng.lng());
    }
  }

  const onSetMapRef = m => {
    map.current = m;
    setMapInitialized(true);
  }

  const getPlaceLatLng = place => {
    if (!place?.geometry) return { lat: null, lng: null }; 
    const location = place.geometry.location.toJSON();
    return { lat: location.lat, lng: location.lng };
  }

  const onAutoCompletePlaceChanged = () => {
    const place = autoCompleteRef.current.getPlace();
    console.log(place)
    if (!place?.geometry) return; // User entered the name of a Place that was not suggested and pressed the Enter key, or the Place Details request failed.
    setAutoCompletePlace(place);
    // updateLatLng(getPlaceLatLng(place).lat, getPlaceLatLng(place).lng);
  }

  const getAddressComponent = (place, component) => {
    let components = place.address_components.filter(c => c.types.length > 0 && c.types[0] === component);
    if (components.length === 0) {
      return "";
    } else {
      if (component === "administrative_area_level_2" && components[0].short_name?.endsWith(" County")) {
        return components[0].short_name.substring(0, components[0].short_name.length - " County".length);
      }
      return components[0].short_name;
    }
  }

  const handleReplaceCurrentAddress = async () => {
    if (!autoCompletePlace?.address_components) return;
    const location = getPlaceLatLng(autoCompletePlace);
    const newAddress = {
      latitude: location.lat,
      longitude: location.lng,
      street: getAddressComponent(autoCompletePlace, 'street_number') + ' ' + getAddressComponent(autoCompletePlace, 'route'),
      city: getAddressComponent(autoCompletePlace, 'locality'),
      state: getAddressComponent(autoCompletePlace, 'administrative_area_level_1'),
      zipCode: getAddressComponent(autoCompletePlace, 'postal_code')
    }
    await updateLatLng(newAddress.latitude, newAddress.longitude)
    replaceCurrentAddress(newAddress);
    setAutoCompletePlace(null);
  }

  const onAutoCompletePlaceMarkerClick = () => {
    updateLatLng(getPlaceLatLng(autoCompletePlace).lat, getPlaceLatLng(autoCompletePlace).lng);
  }

  const isProperty = place => {
    return place.types.includes('street_address') || place.types.includes('premise')
  }

  // const getOpacity = () => {
  //   // if (accuracy === window.google.maps.GeocoderLocationType.ROOFTOP || accuracy === "USER_SELECTED" || accuracy === "SEARCH_RESULT")
  //   //   return 1;
  //   // return 0.5;
  //   return 1;
  // }

  return (
    <GoogleMap
      onLoad={onSetMapRef}
      mapContainerStyle={{ height: `100%` }}
      zoom={4}
      center={{ lat: currentPin?.lat || US_CENTER_LAT, lng: currentPin?.lng || US_CENTER_LNG }}
      onClick={onMapClick}
      options={{
        mapTypeControlOptions: { style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU },
        cameraControl: false,
        zoomControl: true
      }}
    >
      <>
        {hasPin ? <Marker position={{ lat: currentPin?.lat, lng: currentPin?.lng }} draggable={true} onDragEnd={onMarkerDragEnd} opacity={1} title='Location to Create' zIndex={2}/> : null}
        {autoCompletePlace ? <Marker position={getPlaceLatLng(autoCompletePlace)} onClick={onAutoCompletePlaceMarkerClick} opacity={0.6} title='Search Result' zIndex={1}/> : null}
        {autoCompleteEnabled &&
          <MapControl map={map.current ?? null} position={window.google.maps.ControlPosition.TOP} className='mapControlMargin'>
            <div className={classes.controlContainer}>
              <AutoComplete
                ref={autoCompleteRef}
                onPlaceChanged={onAutoCompletePlaceChanged}
              >
                <input type="text" placeholder="Search for an Address" className={classes.searchBox} />
              </AutoComplete>
              <MapControlButton title='Update address fields with current address' onClick={handleReplaceCurrentAddress} text={'Overwrite Address'} disabled={!(autoCompletePlace && isProperty(autoCompletePlace))}/>
            </div>
          </MapControl>
        }
      </>
    </GoogleMap>
  )
};

const styles = theme => ({
  controlContainer: {
    display: 'flex'
  },
  searchBox: {
    boxSizing: `border-box`,
    border: `1px solid rgba(0, 0, 0, 0.23)`,
    width: `500px`,
    height: `38px`,
    padding: `0 12px`,
    boxShadow: `rgba(0, 0, 0, 0.3) 0px 1px 4px -1px`,
    fontSize: `14px`,
    outline: `none`,
    textOverflow: `ellipsis`,
    borderRadius: 4,
    marginRight: 4
  }
});

export default compose(
  withStyles(styles)
)(LocationVerificationMap);